import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { invoicesAPI, paymentSettingsAPI, ordersAPI, customersAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Sheet, SheetContent } from '../../components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Skeleton } from '../../components/ui/skeleton';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
  Plus, FileText, Copy, ExternalLink, Trash2, CheckCircle, XCircle,
  CreditCard, Building2, Phone, Mail, Banknote, Settings2,
  Receipt, Clock, CircleDollarSign, Eye, Link2, Edit, X, ChevronDown, Search, Check, UserPlus
} from 'lucide-react';
import { formatCurrency, formatDateTime, cn } from '../../lib/utils';
import PaywallDialog from '../../components/PaywallDialog';

const INVOICE_STATUSES = {
  new: { label: 'Новый', color: 'bg-blue-500', textColor: 'text-blue-700 bg-blue-50 border-blue-200' },
  paid: { label: 'Оплачен', color: 'bg-emerald-500', textColor: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  cancelled: { label: 'Отменён', color: 'bg-red-500', textColor: 'text-red-700 bg-red-50 border-red-200' },
};

/* СБП (Система Быстрых Платежей) official-style icon */
const SbpIcon = ({ className, style, ...props }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} style={{ width: '1em', height: '1em', ...style }} {...props}>
    <path d="M5.5 7.5L12 4L18.5 7.5" stroke="#F26126" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5.5 12L12 8.5L18.5 12" stroke="#1D1346" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5.5 16.5L12 13L18.5 16.5" stroke="#5B57A2" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5.5 16.5L12 20L18.5 16.5" stroke="#21A038" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PAYMENT_METHOD_TYPES = [
  { value: 'card', label: 'Перевод по номеру карты', icon: CreditCard },
  { value: 'bank', label: 'По реквизитам', icon: Building2 },
  { value: 'sbp', label: 'СБП', icon: SbpIcon },
  { value: 'other', label: 'Другое', icon: CircleDollarSign },
];

export default function InvoicesPage() {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'invoices';
  const [tab, setTab] = useState(defaultTab);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [editInvoice, setEditInvoice] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [paywallOpen, setPaywallOpen] = useState(false);

  // Payment Settings State
  const [paymentSettings, setPaymentSettings] = useState({
    company_name: '',
    company_inn: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    payment_methods: [],
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const loadInvoices = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      const res = await invoicesAPI.getAll(params);
      setInvoices(res.data);
    } catch (error) {
      toast.error('Ошибка загрузки счетов');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  const loadPaymentSettings = useCallback(async () => {
    try {
      const res = await paymentSettingsAPI.get();
      setPaymentSettings(res.data);
    } catch (error) {
      console.error('Error loading payment settings', error);
    }
  }, []);

  useEffect(() => {
    loadInvoices();
    loadPaymentSettings();
  }, [loadInvoices, loadPaymentSettings]);

  // Handle URL params for view/edit (from order detail sheet)
  useEffect(() => {
    const viewId = searchParams.get('view');
    const editId = searchParams.get('edit');
    if (invoices.length > 0) {
      if (viewId) {
        const inv = invoices.find(i => i.id === viewId);
        if (inv) setViewInvoice(inv);
      }
      if (editId) {
        const inv = invoices.find(i => i.id === editId);
        if (inv) setEditInvoice(inv);
      }
    }
  }, [invoices, searchParams]);

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm('Удалить счёт? Это действие нельзя отменить.')) return;
    try {
      await invoicesAPI.delete(id);
      toast.success('Счёт удалён');
      loadInvoices();
      setViewInvoice(null);
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await invoicesAPI.update(id, { status: newStatus });
      toast.success(newStatus === 'paid' ? 'Счёт отмечен как оплаченный' : 'Статус обновлён');
      loadInvoices();
      if (viewInvoice?.id === id) {
        const res = await invoicesAPI.getOne(id);
        setViewInvoice(res.data);
      }
    } catch (error) {
      toast.error('Ошибка обновления');
    }
  };

  const copyPublicLink = (publicId) => {
    const url = `${window.location.origin}/invoice/${publicId}`;
    navigator.clipboard.writeText(url);
    toast.success('Ссылка скопирована');
  };

  const handleSavePaymentSettings = async () => {
    setSavingSettings(true);
    try {
      await paymentSettingsAPI.update(paymentSettings);
      toast.success('Настройки оплаты сохранены');
      setTab('invoices');
    } catch (error) {
      toast.error('Ошибка сохранения');
    } finally {
      setSavingSettings(false);
    }
  };

  const addPaymentMethod = () => {
    setPaymentSettings(prev => ({
      ...prev,
      payment_methods: [
        ...prev.payment_methods,
        { id: crypto.randomUUID(), type: 'card', name: '', details: '', enabled: true }
      ],
    }));
  };

  const updatePaymentMethod = (index, field, value) => {
    setPaymentSettings(prev => {
      const methods = [...prev.payment_methods];
      methods[index] = { ...methods[index], [field]: value };
      return { ...prev, payment_methods: methods };
    });
  };

  const removePaymentMethod = (index) => {
    setPaymentSettings(prev => ({
      ...prev,
      payment_methods: prev.payment_methods.filter((_, i) => i !== index),
    }));
  };

  // Stats
  const stats = {
    total: invoices.length,
    new: invoices.filter(i => i.status === 'new').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    totalAmount: invoices.reduce((sum, i) => sum + (i.amount || 0), 0),
    paidAmount: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.amount || 0), 0),
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Счета</h1>
          <p className="text-sm text-muted-foreground">Выставляйте счета и отслеживайте оплаты</p>
        </div>
        <div className="flex gap-2">
          {tab === 'settings' ? (
            <Button variant="outline" onClick={() => setTab('invoices')} data-testid="back-to-invoices-btn">
              <Receipt className="h-4 w-4 mr-2" />
              Назад к счетам
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setTab('settings')} data-testid="payment-settings-btn">
              <Settings2 className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Настройки оплаты</span>
              <span className="sm:hidden">Настройки</span>
            </Button>
          )}
          <Button onClick={() => setCreateOpen(true)} data-testid="create-invoice-btn">
            <Plus className="h-4 w-4 mr-2" />
            Создать счёт
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsContent value="invoices" className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Card className="px-3 py-2.5">
              <p className="text-xs text-muted-foreground">Всего счетов</p>
              <p className="text-xl font-bold">{stats.total}</p>
            </Card>
            <Card className="px-3 py-2.5">
              <p className="text-xs text-muted-foreground">Ожидают оплаты</p>
              <p className="text-xl font-bold text-blue-600">{stats.new}</p>
            </Card>
            <Card className="px-3 py-2.5">
              <p className="text-xs text-muted-foreground">Оплачено</p>
              <p className="text-xl font-bold text-emerald-600">{stats.paid}</p>
            </Card>
            <Card className="px-3 py-2.5">
              <p className="text-xs text-muted-foreground">Сумма оплат</p>
              <p className="text-xl font-bold">{formatCurrency(stats.paidAmount)}</p>
            </Card>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Фильтр" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все счета</SelectItem>
                <SelectItem value="new">Новые</SelectItem>
                <SelectItem value="paid">Оплаченные</SelectItem>
                <SelectItem value="cancelled">Отменённые</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Invoice List */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}
            </div>
          ) : invoices.length === 0 ? (
            <Card className="p-12 text-center">
              <Receipt className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Нет счетов</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Создайте первый счёт вручную или из карточки заказа
              </p>
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Создать счёт
              </Button>
            </Card>
          ) : (
            <>
              {/* Mobile: card list */}
              <div className="md:hidden flex flex-col gap-4">
                {invoices.map(invoice => (
                  <Card
                    key={invoice.id}
                    className="hover:shadow-md transition-shadow"
                  >
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm font-semibold text-primary">Счёт №{invoice.invoice_number}</span>
                          {invoice.order_info?.form_schema_name && (
                            <Badge variant="outline" className="text-xs">
                              {invoice.order_info.form_schema_name}
                            </Badge>
                          )}
                        </div>
                        <Select value={invoice.status} onValueChange={(value) => handleStatusChange(invoice.id, value)}>
                          <SelectTrigger className="h-auto w-auto border-0 p-0 bg-transparent shadow-none focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                            <Badge variant="outline" className={cn('text-xs border cursor-pointer', INVOICE_STATUSES[invoice.status]?.textColor)}>
                              {INVOICE_STATUSES[invoice.status]?.label}
                              <ChevronDown className="h-3 w-3 ml-1" />
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(INVOICE_STATUSES).map(([key, { label, textColor }]) => (
                              <SelectItem key={key} value={key}>
                                <Badge variant="outline" className={cn('text-xs border', textColor)}>{label}</Badge>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {(invoice.customer_name || (invoice.order_id && invoice.order_info)) && (
                        <div className="flex items-center gap-2 text-sm">
                          {invoice.customer_name && (
                            <span className="font-medium truncate">{invoice.customer_name}</span>
                          )}
                          {invoice.customer_name && invoice.order_id && invoice.order_info && (
                            <span className="text-muted-foreground">·</span>
                          )}
                          {invoice.order_id && invoice.order_info && (
                            <span className="text-muted-foreground truncate">Заказ {invoice.order_info.order_number_display || `#${invoice.order_info.order_number}`}</span>
                          )}
                        </div>
                      )}
                      {invoice.description && (
                        <p className="text-xs text-muted-foreground line-clamp-1 truncate">{invoice.description}</p>
                      )}
                      <div className="flex items-center justify-between pt-3 border-t border-border/50">
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-muted-foreground">{formatDateTime(invoice.created_at)}</span>
                          <span className="font-bold text-base">{formatCurrency(invoice.amount)}</span>
                        </div>
                        <div className="flex items-center gap-0.5 shrink-0">
                          <Button variant="ghost" size="icon" className="h-9 w-9 touch-manipulation" onClick={() => setViewInvoice(invoice)} title="Просмотреть счёт">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-9 w-9 touch-manipulation" onClick={() => setEditInvoice(invoice)} title="Редактировать">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-9 w-9 touch-manipulation" onClick={() => copyPublicLink(invoice.public_id)} title="Копировать ссылку">
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive hover:bg-destructive/10 touch-manipulation" onClick={() => handleDeleteInvoice(invoice.id)} title="Удалить">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Desktop: card grid */}
              <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                    {invoices.map(invoice => (
                      <div
                        key={invoice.id}
                        className="p-3.5 bg-card rounded-lg border shadow-sm hover:shadow-md hover:border-primary/30 transition-all"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-mono text-sm font-bold text-primary">Счёт №{invoice.invoice_number}</span>
                            {invoice.order_info?.form_schema_name && (
                              <Badge variant="outline" className="text-xs">
                                {invoice.order_info.form_schema_name}
                              </Badge>
                            )}
                          </div>
                          <Select value={invoice.status} onValueChange={(value) => handleStatusChange(invoice.id, value)}>
                            <SelectTrigger className="h-auto w-auto border-0 p-0 bg-transparent shadow-none focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                              <Badge variant="outline" className={cn('text-xs border cursor-pointer hover:opacity-80 transition-opacity', INVOICE_STATUSES[invoice.status]?.textColor)}>
                                {INVOICE_STATUSES[invoice.status]?.label}
                                <ChevronDown className="h-3 w-3 ml-1" />
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(INVOICE_STATUSES).map(([key, { label, textColor }]) => (
                                <SelectItem key={key} value={key}>
                                  <Badge variant="outline" className={cn('text-xs border', textColor)}>{label}</Badge>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {(invoice.customer_name || (invoice.order_id && invoice.order_info)) && (
                          <div className="flex items-center gap-1.5 text-sm mb-1">
                            {invoice.customer_name && (
                              <span className="font-medium truncate">{invoice.customer_name}</span>
                            )}
                            {invoice.customer_name && invoice.order_id && invoice.order_info && (
                              <span className="text-muted-foreground">·</span>
                            )}
                            {invoice.order_id && invoice.order_info && (
                              <span className="text-muted-foreground truncate">Заказ {invoice.order_info.order_number_display || `#${invoice.order_info.order_number}`}</span>
                            )}
                          </div>
                        )}

                        {invoice.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1 truncate mb-1">{invoice.description}</p>
                        )}

                        <div className="text-xs text-muted-foreground mb-2">
                          <div>Создан: {formatDateTime(invoice.created_at)}</div>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-border/50">
                          <div>
                            <div className="text-xs text-muted-foreground">Сумма</div>
                            <div className="font-bold text-base">{formatCurrency(invoice.amount)}</div>
                          </div>
                          <div className="flex items-center gap-0">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setViewInvoice(invoice)} title="Просмотреть счёт">
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditInvoice(invoice)} title="Редактировать">
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyPublicLink(invoice.public_id)} title="Копировать ссылку">
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:bg-destructive/10" onClick={() => handleDeleteInvoice(invoice.id)} title="Удалить">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="settings" className="space-y-6 mt-4">
          <PaymentSettingsForm
            settings={paymentSettings}
            setSettings={setPaymentSettings}
            onSave={handleSavePaymentSettings}
            saving={savingSettings}
            addMethod={addPaymentMethod}
            updateMethod={updatePaymentMethod}
            removeMethod={removePaymentMethod}
          />
        </TabsContent>
      </Tabs>

      {/* Create Invoice Dialog */}
      <CreateInvoiceDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          loadInvoices();
        }}
        onPaywall={() => setPaywallOpen(true)}
      />

      {/* View Invoice Sheet */}
      {viewInvoice && (
        <ViewInvoiceSheet
          invoice={viewInvoice}
          onClose={() => setViewInvoice(null)}
          onEdit={() => {
            const inv = viewInvoice;
            setViewInvoice(null);
            setEditInvoice(inv);
          }}
          onStatusChange={handleStatusChange}
          onDelete={handleDeleteInvoice}
          onCopyLink={copyPublicLink}
        />
      )}

      {/* Edit Invoice Sheet */}
      {editInvoice && (
        <EditInvoiceSheet
          invoice={editInvoice}
          onClose={() => setEditInvoice(null)}
          onSaved={() => {
            setEditInvoice(null);
            loadInvoices();
          }}
        />
      )}

      <PaywallDialog
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        resourceType="invoices"
        limit={3}
      />
    </div>
  );
}

// ============ Create Invoice Dialog ============
function CreateInvoiceDialog({ open, onClose, onCreated, orderData: externalOrderData, onPaywall }) {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [amountType, setAmountType] = useState('custom');
  const [createdInvoice, setCreatedInvoice] = useState(null);

  // Order & customer selection
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showNewCustomer, setShowNewCustomer] = useState(false);

  // Popover states
  const [orderPopoverOpen, setOrderPopoverOpen] = useState(false);
  const [customerPopoverOpen, setCustomerPopoverOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const customerPanelRef = React.useRef(null);

  useEffect(() => {
    if (customerPopoverOpen && customerPanelRef.current) {
      setTimeout(() => customerPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
    }
  }, [customerPopoverOpen]);

  // Load orders and customers when dialog opens
  useEffect(() => {
    if (open && !externalOrderData) {
      ordersAPI.getAll().then(res => setOrders(res.data || [])).catch(() => {});
      customersAPI.getAll({ limit: 1000 }).then(res => setCustomers(res.data || [])).catch(() => {});
    }
  }, [open, externalOrderData]);

  // Set external order data
  useEffect(() => {
    if (externalOrderData) {
      setSelectedOrder(externalOrderData);
      setCustomerName(externalOrderData.customer_name || '');
      setCustomerPhone(externalOrderData.customer_phone || '');
      setCustomerEmail(externalOrderData.customer_email || '');
      if (externalOrderData.total) {
        setAmount(String(externalOrderData.total));
        setAmountType('full');
      }
    }
  }, [externalOrderData]);

  // When order selected from dropdown
  useEffect(() => {
    if (selectedOrderId && selectedOrderId !== 'none') {
      const order = orders.find(o => o.id === selectedOrderId);
      if (order) {
        setSelectedOrder({
          order_id: order.id,
          order_number: order.order_number,
          order_number_display: order.order_number_display,
          total: order.total || 0,
          prepayment: order.prepayment || 0,
          customer_id: order.customer_id || null,
          customer_name: order.customer_name || '',
          customer_phone: order.customer_phone || '',
          customer_email: order.customer_email || '',
        });
        setCustomerName(order.customer_name || '');
        setCustomerPhone(order.customer_phone || '');
        setCustomerEmail(order.customer_email || '');
        setAmount(String(order.total || 0));
        setAmountType('full');
        setSelectedCustomerId('');
        setShowNewCustomer(false);
      }
    } else if (selectedOrderId === 'none') {
      setSelectedOrder(null);
      setAmountType('custom');
      setAmount('');
    }
  }, [selectedOrderId, orders]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  // When customer selected from dropdown (without order)
  const prevCustomerIdRef = React.useRef('');
  useEffect(() => {
    if (selectedCustomerId === prevCustomerIdRef.current) return;
    prevCustomerIdRef.current = selectedCustomerId;

    if (selectedCustomerId && selectedCustomerId !== 'new') {
      const customer = customers.find(c => c.id === selectedCustomerId);
      if (customer) {
        setCustomerName(customer.name || '');
        setCustomerPhone(customer.phone || '');
        setCustomerEmail(customer.email || '');
        setShowNewCustomer(false);
      }
    } else if (selectedCustomerId === 'new') {
      setCustomerName('');
      setCustomerPhone('');
      setCustomerEmail('');
      setShowNewCustomer(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId]);

  useEffect(() => {
    if (open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const orderData = selectedOrder || externalOrderData;

  const handleAmountTypeChange = (type) => {
    setAmountType(type);
    if (!orderData) return;
    switch (type) {
      case 'full':
        setAmount(String(orderData.total || 0));
        break;
      case 'prepayment':
        setAmount(String(orderData.prepayment || 0));
        break;
      case 'remaining':
        setAmount(String((orderData.total || 0) - (orderData.prepayment || 0)));
        break;
      case 'custom':
        setAmount('');
        break;
      default:
        break;
    }
  };

  const resetForm = () => {
    setAmount('');
    setDescription('');
    setCustomerName('');
    setCustomerPhone('');
    setCustomerEmail('');
    setAmountType('custom');
    setCreatedInvoice(null);
    setSelectedOrderId('');
    setSelectedCustomerId('');
    setSelectedOrder(null);
    setShowNewCustomer(false);
  };

  const handleCreate = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Укажите сумму счёта');
      return;
    }
    setCreating(true);
    try {
      // Determine customer_id: from selected customer, from order, or from newly created customer
      let resolvedCustomerId = null;
      if (selectedCustomerId && selectedCustomerId !== 'new') {
        resolvedCustomerId = selectedCustomerId;
      } else if (orderData?.customer_id) {
        resolvedCustomerId = orderData.customer_id;
      }

      // If new customer — try to save to customers and capture ID
      if (showNewCustomer && customerName) {
        try {
          const newCust = await customersAPI.create({
            name: customerName,
            phone: customerPhone,
            email: customerEmail,
          });
          if (newCust?.data?.id) {
            resolvedCustomerId = newCust.data.id;
          }
        } catch (e) {
          // Limit reached or other error — just skip, invoice still gets customer data as text
        }
      }

      const res = await invoicesAPI.create({
        order_id: orderData?.order_id || null,
        amount: parseFloat(amount),
        amount_type: amountType,
        description,
        customer_id: resolvedCustomerId,
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail,
      });
      toast.success('Счёт создан');
      setCreatedInvoice(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        if (onPaywall) onPaywall();
        else toast.error(error.response?.data?.detail || 'Лимит демо-версии исчерпан');
      } else {
        toast.error('Ошибка создания счёта');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onCreated();
  };

  const handleOpenInvoice = () => {
    if (createdInvoice?.public_id) {
      window.open(`${window.location.origin}/invoice/${createdInvoice.public_id}`, '_blank');
    }
    handleClose();
  };

  const copyLink = () => {
    if (createdInvoice?.public_id) {
      navigator.clipboard.writeText(`${window.location.origin}/invoice/${createdInvoice.public_id}`);
      toast.success('Ссылка скопирована');
    }
  };

  // Success screen after creation
  if (createdInvoice) {
    const publicUrl = `${window.location.origin}/invoice/${createdInvoice.public_id}`;
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-sm">
          <div className="text-center py-4">
            <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-bold mb-1">Счёт №{createdInvoice.invoice_number} создан</h3>
            <p className="text-sm text-muted-foreground mb-5">
              Сумма: {formatCurrency(createdInvoice.amount)}
            </p>

            {/* Public link for client */}
            <div className="text-left mb-5">
              <p className="text-xs font-medium text-muted-foreground mb-2">Ссылка на оплату для клиента</p>
              <div className="flex items-center gap-2 bg-muted/50 rounded-lg p-2">
                <Input
                  value={publicUrl}
                  readOnly
                  className="text-xs h-9"
                />
                <Button variant="outline" size="icon" className="shrink-0 h-9 w-9" onClick={copyLink} title="Копировать ссылку">
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="shrink-0 h-9 w-9" asChild title="Открыть страницу оплаты">
                  <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>

            {/* Action: go to invoice in the app */}
            <Button
              variant="default"
              className="w-full"
              onClick={() => {
                handleClose();
                window.location.href = `/invoices?view=${createdInvoice.id}`;
              }}
            >
              <Eye className="h-4 w-4 mr-2" />
              Перейти к счёту
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle>Создать счёт</DialogTitle>
          <DialogDescription>
            {orderData ? `На основе заказа ${orderData.order_number_display || '#' + orderData.order_number}` : 'Новый счёт на оплату'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 overflow-y-auto flex-1 px-6 pb-2">

          {/* Order selection (only when not from external order) */}
          {!externalOrderData && (
            <div className="space-y-2">
              <Label>Заказ</Label>
              <Popover open={orderPopoverOpen} onOpenChange={setOrderPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between font-normal h-10">
                    <span className="truncate">
                      {selectedOrderId && selectedOrderId !== 'none'
                        ? (() => {
                            const o = orders.find(o => o.id === selectedOrderId);
                            return o ? `${o.order_number_display || '#' + o.order_number} · ${o.form_schema_name || ''} · ${o.customer_name || ''} · ${formatCurrency(o.total || 0)}` : 'Без заказа';
                          })()
                        : 'Без заказа'
                      }
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Поиск заказа..." />
                    <CommandList>
                      <CommandEmpty>Заказы не найдены</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          onSelect={() => {
                            setSelectedOrderId('none');
                            setOrderPopoverOpen(false);
                          }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", (!selectedOrderId || selectedOrderId === 'none') ? "opacity-100" : "opacity-0")} />
                          Без заказа
                        </CommandItem>
                        {orders.map(order => (
                          <CommandItem
                            key={order.id}
                            value={`${order.order_number_display || order.order_number} ${order.form_schema_name || ''} ${order.customer_name || ''}`}
                            onSelect={() => {
                              setSelectedOrderId(order.id);
                              setOrderPopoverOpen(false);
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", selectedOrderId === order.id ? "opacity-100" : "opacity-0")} />
                            <div className="flex flex-col min-w-0">
                              <span className="text-sm truncate">{order.order_number_display || `#${order.order_number}`} {order.form_schema_name ? `· ${order.form_schema_name}` : ''}</span>
                              <span className="text-xs text-muted-foreground truncate">
                                {order.customer_name || 'Без клиента'} · {formatCurrency(order.total || 0)}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Amount type buttons (when order is selected) */}
          {orderData && (
            <div className="space-y-1.5">
              <Label>Сумма</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { value: 'full', label: `Полная: ${formatCurrency(orderData.total)}` },
                  { value: 'prepayment', label: `Предоплата: ${formatCurrency(orderData.prepayment)}` },
                  { value: 'remaining', label: `Доплата: ${formatCurrency((orderData.total || 0) - (orderData.prepayment || 0))}` },
                  { value: 'custom', label: 'Свой вариант' },
                ].map(opt => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={amountType === opt.value ? 'default' : 'outline'}
                    size="sm"
                    className="text-xs justify-start h-8"
                    onClick={() => handleAmountTypeChange(opt.value)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Сумма счёта *</Label>
            <Input
              type="number"
              placeholder="0"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); if (orderData) setAmountType('custom'); }}
              readOnly={orderData && amountType !== 'custom'}
              className="h-9"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Описание / комментарий</Label>
            <Textarea
              placeholder="За что выставлен счёт..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={1}
              className="min-h-[36px] resize-y"
            />
          </div>

          <Separator />

          {/* Customer section */}
          {!orderData && !externalOrderData && (
            <div className="space-y-2">
              <Label>Клиент</Label>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between font-normal h-10 overflow-hidden"
                onClick={() => { setCustomerPopoverOpen(!customerPopoverOpen); setCustomerSearch(''); }}
              >
                <span className="truncate">
                  {selectedCustomerId === 'new'
                    ? '+ Новый клиент'
                    : selectedCustomerId
                      ? (() => {
                          const c = customers.find(c => c.id === selectedCustomerId);
                          return c ? `${c.name}${c.phone ? ` · ${c.phone}` : ''}` : 'Выберите клиента';
                        })()
                      : 'Выберите клиента'
                  }
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
              {customerPopoverOpen && (
                <div ref={customerPanelRef} className="rounded-md border bg-popover shadow-sm w-full overflow-hidden">
                  <div className="p-3 border-b">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Поиск клиента..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        className="pl-9"
                        autoFocus
                      />
                      {customerSearch && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                          onClick={() => setCustomerSearch('')}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="max-h-[220px] overflow-y-auto overscroll-contain p-2 pb-3">
                    <div
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover:bg-muted",
                        selectedCustomerId === 'new' && "bg-muted"
                      )}
                      onClick={() => {
                        setSelectedCustomerId('new');
                        setCustomerPopoverOpen(false);
                        setCustomerSearch('');
                      }}
                    >
                      <UserPlus className="h-4 w-4 shrink-0" />
                      <span>+ Новый клиент</span>
                    </div>
                    {(() => {
                      const filtered = customers.filter(c => {
                        if (!customerSearch) return true;
                        const q = customerSearch.toLowerCase();
                        return (c.name || '').toLowerCase().includes(q) ||
                               (c.phone || '').toLowerCase().includes(q) ||
                               (c.email || '').toLowerCase().includes(q);
                      });
                      if (filtered.length === 0) {
                        return (
                          <div className="px-3 py-6 text-center text-muted-foreground text-sm">
                            {customerSearch ? 'Клиенты не найдены' : 'Нет клиентов'}
                          </div>
                        );
                      }
                      return filtered.map(c => (
                        <div
                          key={c.id}
                          className={cn(
                            "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover:bg-muted",
                            selectedCustomerId === c.id && "bg-muted"
                          )}
                          onClick={() => {
                            setSelectedCustomerId(c.id);
                            setCustomerPopoverOpen(false);
                            setCustomerSearch('');
                          }}
                        >
                          <Check className={cn("h-4 w-4 shrink-0", selectedCustomerId === c.id ? "opacity-100" : "opacity-0")} />
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm truncate">{c.name}</span>
                            {(c.phone || c.email) && (
                              <span className="text-xs text-muted-foreground truncate">
                                {[c.phone, c.email].filter(Boolean).join(' · ')}
                              </span>
                            )}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                  {customers.length > 0 && (
                    <div className="p-2 border-t text-xs text-muted-foreground text-center">
                      Показано {customers.filter(c => {
                        if (!customerSearch) return true;
                        const q = customerSearch.toLowerCase();
                        return (c.name || '').toLowerCase().includes(q) || (c.phone || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
                      }).length} из {customers.length} клиентов
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Customer info fields (show when: order selected OR new customer OR customer selected) */}
          {(orderData || showNewCustomer || selectedCustomerId) && (
            <>
              <div className="space-y-1.5">
                <Label>Имя клиента</Label>
                <Input
                  placeholder="Иван Петров"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  readOnly={!!orderData}
                  className="h-9"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label>Телефон</Label>
                  <Input
                    placeholder="+7 900 123-45-67"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    readOnly={!!orderData}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input
                    placeholder="email@example.com"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    readOnly={!!orderData}
                    className="h-9"
                  />
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter className="flex flex-row justify-between gap-2 px-6 py-4 border-t shrink-0">
          <Button variant="outline" size="lg" onClick={onClose}>Отмена</Button>
          <Button size="lg" onClick={handleCreate} disabled={creating}>
            {creating ? 'Создание...' : 'Создать счёт'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ View Invoice Sheet ============
function ViewInvoiceSheet({ invoice, onClose, onEdit, onStatusChange, onDelete, onCopyLink }) {
  const navigate = useNavigate();
  const publicUrl = `${window.location.origin}/invoice/${invoice.public_id}`;

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-md p-0 overflow-y-auto [&>button]:hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 pt-8 pb-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-background/80 transition-colors z-10"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>

          <div className="flex items-start gap-4 mt-2">
            <div className="h-14 w-14 rounded-xl bg-primary/20 flex items-center justify-center border-2 border-background shadow-sm">
              <Receipt className="h-7 w-7 text-primary" />
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <h2 className="text-lg font-bold">Счёт №{invoice.invoice_number}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {formatDateTime(invoice.created_at)}
                {invoice.paid_at && ` · Оплачен ${formatDateTime(invoice.paid_at)}`}
              </p>
              <Badge variant="outline" className={cn('text-xs border mt-2', INVOICE_STATUSES[invoice.status]?.textColor)}>
                {INVOICE_STATUSES[invoice.status]?.label}
              </Badge>
            </div>
          </div>
        </div>

        <div className="px-6 pt-6 pb-8">
          {/* Amount Card */}
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 p-4">
            <CircleDollarSign className="absolute -right-2 -bottom-2 h-12 w-12 text-emerald-500/10" />
            <div className="relative">
              <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">Сумма к оплате</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(invoice.amount)}</p>
            </div>
          </div>

          {/* Status Change */}
          <div className="mt-3">
            <Select value={invoice.status} onValueChange={(val) => onStatusChange(invoice.id, val)}>
              <SelectTrigger className="w-full h-11">
                <div className="flex items-center gap-2">
                  <div className={cn('h-2.5 w-2.5 rounded-full', INVOICE_STATUSES[invoice.status]?.color)} />
                  <span className="text-xs text-muted-foreground mr-1">Статус:</span>
                  <span className="font-medium">{INVOICE_STATUSES[invoice.status]?.label}</span>
                </div>
              </SelectTrigger>
              <SelectContent>
                {Object.entries(INVOICE_STATUSES).map(([key, { label, color }]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <div className={cn('h-2.5 w-2.5 rounded-full', color)} />
                      {label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          {invoice.description && (
            <div className="mt-8">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                <FileText className="h-3.5 w-3.5" />
                Описание
              </h4>
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="p-4">
                  <p className="text-sm whitespace-pre-wrap">{invoice.description}</p>
                </div>
              </div>
            </div>
          )}

          {/* Customer */}
          {(invoice.customer_name || invoice.customer_phone || invoice.customer_email) && (
            <div className="mt-6">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-2">
                <Mail className="h-3.5 w-3.5" />
                Клиент
              </h4>
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div
                  className="p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => {
                    if (invoice.customer_id) {
                      onClose();
                      navigate(`/customers?view=${invoice.customer_id}`);
                    }
                  }}
                >
                  {invoice.customer_name && (
                    <p className={`font-medium text-sm ${invoice.customer_id ? 'text-primary' : ''}`}>
                      {invoice.customer_name}{invoice.customer_id ? ' →' : ''}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                    {invoice.customer_phone && (
                      <span className="flex items-center gap-1.5 text-sm text-muted-foreground" onClick={(e) => e.stopPropagation()}>
                        <Phone className="h-3.5 w-3.5 text-green-600" />
                        <a href={`tel:${invoice.customer_phone}`} className="hover:text-foreground transition-colors">{invoice.customer_phone}</a>
                      </span>
                    )}
                    {invoice.customer_email && (
                      <span className="flex items-center gap-1.5 text-sm text-muted-foreground" onClick={(e) => e.stopPropagation()}>
                        <Mail className="h-3.5 w-3.5 text-blue-600" />
                        <a href={`mailto:${invoice.customer_email}`} className="hover:text-foreground transition-colors">{invoice.customer_email}</a>
                      </span>
                    )}
                  </div>
                  {invoice.customer_id && (
                    <p className="text-xs text-muted-foreground mt-1">Нажмите для перехода к клиенту</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Order Info */}
          {invoice.order_info && (
            <div className="mt-6">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-2">
                <FileText className="h-3.5 w-3.5" />
                Заказ
              </h4>
              <div
                className="rounded-xl border border-border bg-card p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => {
                  onClose();
                  navigate(`/orders?view=${invoice.order_id}`);
                }}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm text-primary">{invoice.order_info.order_number_display || `#${invoice.order_info.order_number}`} →</span>
                  <span className="font-medium text-sm">{formatCurrency(invoice.order_info.total)}</span>
                </div>
                {invoice.order_info.form_schema_name && (
                  <p className="text-xs text-muted-foreground mt-1">{invoice.order_info.form_schema_name}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">Нажмите для перехода к заказу</p>
              </div>
            </div>
          )}

          {/* Public Link */}
          <div className="mt-8">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
              <Link2 className="h-3.5 w-3.5" />
              Ссылка для клиента
            </h4>
            <div className="flex items-center gap-2">
              <Input value={publicUrl} readOnly className="text-xs" />
              <Button variant="outline" size="icon" className="shrink-0" onClick={() => onCopyLink(invoice.public_id)} title="Копировать">
                <Copy className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" className="shrink-0" asChild title="Открыть">
                <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8">
            <Button
              onClick={onEdit}
              className="w-full h-12 font-medium text-base"
              variant="default"
            >
              <Edit className="h-4 w-4 mr-2" />
              Редактировать счёт
            </Button>
          </div>

          <div className="mt-3">
            <Button
              variant="outline"
              className="w-full h-11 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => onDelete(invoice.id)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Удалить счёт
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ============ Edit Invoice Sheet ============
function EditInvoiceSheet({ invoice, onClose, onSaved }) {
  const [amount, setAmount] = useState(String(invoice.amount || ''));
  const [description, setDescription] = useState(invoice.description || '');
  const [customerName, setCustomerName] = useState(invoice.customer_name || '');
  const [customerPhone, setCustomerPhone] = useState(invoice.customer_phone || '');
  const [customerEmail, setCustomerEmail] = useState(invoice.customer_email || '');
  const [status, setStatus] = useState(invoice.status || 'new');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Укажите сумму');
      return;
    }
    setSaving(true);
    try {
      await invoicesAPI.update(invoice.id, {
        amount: parseFloat(amount),
        description,
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail,
        status,
      });
      toast.success('Счёт обновлён');
      onSaved();
    } catch (error) {
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-md p-0 overflow-y-auto [&>button]:hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 pt-8 pb-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-background/80 transition-colors z-10"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>

          <div className="flex items-start gap-4 mt-2">
            <div className="h-14 w-14 rounded-xl bg-primary/20 flex items-center justify-center border-2 border-background shadow-sm">
              <Edit className="h-7 w-7 text-primary" />
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <h2 className="text-lg font-bold">Редактирование счёта №{invoice.invoice_number}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Создан {formatDateTime(invoice.created_at)}
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 pt-6 pb-8 space-y-5">
          {/* Amount */}
          <div className="space-y-2">
            <Label>Сумма *</Label>
            <Input
              type="number"
              placeholder="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="0.01"
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label>Статус</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(INVOICE_STATUSES).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Описание</Label>
            <Textarea
              placeholder="За что выставлен счёт"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <Separator />

          {/* Customer info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Клиент</h3>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Имя клиента</Label>
                <Input
                  placeholder="Имя"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Телефон</Label>
                <Input
                  placeholder="+7 900 123-45-67"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  placeholder="email@example.com"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Save Buttons */}
          <div className="flex gap-3 pt-4">
            <Button className="flex-1" onClick={handleSave} disabled={saving}>
              {saving ? 'Сохранение...' : 'Сохранить'}
            </Button>
            <Button variant="outline" onClick={onClose}>
              Отмена
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ============ Payment Settings Form ============
function PaymentSettingsForm({ settings, setSettings, onSave, saving, addMethod, updateMethod, removeMethod }) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Реквизиты</CardTitle>
          <CardDescription>Эти данные будут отображаться в публичных счетах</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Название компании / ИП / ФИО</Label>
              <Input
                placeholder="ИП Иванов И.И."
                value={settings.company_name || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, company_name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>ИНН</Label>
              <Input
                placeholder="1234567890"
                value={settings.company_inn || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, company_inn: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Адрес</Label>
              <Input
                placeholder="г. Москва, ул. Примерная, д. 1"
                value={settings.company_address || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, company_address: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Телефон</Label>
              <Input
                placeholder="+7 (999) 123-45-67"
                value={settings.company_phone || ''}
                onChange={(e) => {
                  let val = e.target.value;
                  const digits = val.replace(/\D/g, '');
                  const isRussian = val.startsWith('+7') || val.startsWith('+8') || 
                                    (!val.startsWith('+') && (digits.startsWith('7') || digits.startsWith('8')));
                  if (isRussian && digits.length > 0) {
                    const core = (digits.startsWith('7') || digits.startsWith('8')) ? digits.slice(1) : digits;
                    let formatted = '+7';
                    if (core.length > 0) formatted += ' (' + core.slice(0, 3);
                    if (core.length >= 3) formatted += ') ' + core.slice(3, 6);
                    if (core.length >= 6) formatted += '-' + core.slice(6, 8);
                    if (core.length >= 8) formatted += '-' + core.slice(8, 10);
                    val = formatted;
                  }
                  setSettings(prev => ({ ...prev, company_phone: val }));
                }}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={settings.company_email || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, company_email: e.target.value }))}
                className={settings.company_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(settings.company_email) ? 'border-destructive' : ''}
              />
              {settings.company_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(settings.company_email) && (
                <p className="text-xs text-destructive">Некорректный формат email</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Способы оплаты</CardTitle>
              <CardDescription>Добавьте способы, через которые клиенты могут оплатить</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={addMethod}>
              <Plus className="h-4 w-4 mr-2" />
              Добавить
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.payment_methods?.length === 0 && (
            <div className="text-center py-8 text-muted-foreground text-sm">
              <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Нет способов оплаты</p>
              <p className="text-xs mt-1">Добавьте карту, реквизиты или другой способ</p>
            </div>
          )}
          {settings.payment_methods?.map((method, index) => (
            <div key={method.id || index} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Select
                    value={method.type}
                    onValueChange={(val) => updateMethod(index, 'type', val)}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHOD_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          <span className="flex items-center gap-2">
                            <t.icon className="h-4 w-4" />
                            {t.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={method.enabled}
                      onCheckedChange={(val) => updateMethod(index, 'enabled', val)}
                    />
                    <span className="text-xs text-muted-foreground">{method.enabled ? 'Активен' : 'Скрыт'}</span>
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeMethod(index)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-2">
                {method.type !== 'sbp' && method.type !== 'card' && method.type !== 'bank' && (
                  <Input
                    placeholder="Название (например: Сбербанк)"
                    value={method.name || ''}
                    onChange={(e) => updateMethod(index, 'name', e.target.value)}
                  />
                )}
                {method.type === 'card' && (
                  <div className="space-y-2">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Название банка</Label>
                      <Input
                        placeholder="Например: Сбербанк, Тинькофф"
                        value={method.card_bank || ''}
                        onChange={(e) => updateMethod(index, 'card_bank', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Номер карты</Label>
                      <Input
                        placeholder="0000 0000 0000 0000"
                        value={method.card_number || ''}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, '').slice(0, 16);
                          const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
                          updateMethod(index, 'card_number', formatted);
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Имя получателя (видно клиенту)</Label>
                      <Input
                        placeholder="Иванов Иван Иванович"
                        value={method.card_recipient || ''}
                        onChange={(e) => updateMethod(index, 'card_recipient', e.target.value)}
                      />
                    </div>
                  </div>
                )}
                {method.type === 'sbp' && (
                  <div className="space-y-2">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Телефон для СБП-перевода</Label>
                      <Input
                        placeholder="+7 (999) 123-45-67"
                        value={method.sbp_phone || ''}
                        onChange={(e) => {
                          let val = e.target.value;
                          const digits = val.replace(/\D/g, '');
                          const isRussian = val.startsWith('+7') || val.startsWith('+8') || 
                                            (!val.startsWith('+') && (digits.startsWith('7') || digits.startsWith('8')));
                          if (isRussian && digits.length > 0) {
                            const core = (digits.startsWith('7') || digits.startsWith('8')) ? digits.slice(1) : digits;
                            let formatted = '+7';
                            if (core.length > 0) formatted += ' (' + core.slice(0, 3);
                            if (core.length >= 3) formatted += ') ' + core.slice(3, 6);
                            if (core.length >= 6) formatted += '-' + core.slice(6, 8);
                            if (core.length >= 8) formatted += '-' + core.slice(8, 10);
                            val = formatted;
                          }
                          updateMethod(index, 'sbp_phone', val);
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Банк получателя</Label>
                      <Input
                        placeholder="Например: Сбербанк, Тинькофф"
                        value={method.sbp_bank || ''}
                        onChange={(e) => updateMethod(index, 'sbp_bank', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">ФИО получателя (видно клиенту)</Label>
                      <Input
                        placeholder="Иванов Иван Иванович"
                        value={method.sbp_recipient || ''}
                        onChange={(e) => updateMethod(index, 'sbp_recipient', e.target.value)}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Клиент увидит кнопки банков для быстрого перевода по номеру телефона
                    </p>
                  </div>
                )}
                {method.type !== 'sbp' && method.type !== 'card' && (
                  <Textarea
                    placeholder={
                      method.type === 'bank' ? 'ИНН: ...\nР/с: ...\nБИК: ...\nБанк: ...' :
                      'Описание способа оплаты'
                    }
                    value={method.details || ''}
                    onChange={(e) => updateMethod(index, 'details', e.target.value)}
                    rows={3}
                  />
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={saving}>
          {saving ? 'Сохранение...' : 'Сохранить настройки'}
        </Button>
      </div>
    </div>
  );
}

// Export CreateInvoiceDialog for use in OrderDetailPage
export { CreateInvoiceDialog };
